<template>
  <div class="tml-color-selector" :class="{error: displayError}">
    <div class="flex flex-wrap gap-2" aria-label="Choose a colour">
      <button
        v-for="(color, index) in formattedColors"
        v-if="formattedColors"
        :key="index"
        class="color h-8 hover:outline hover:outline-1"
        :class="{
          'outline !outline-2 outline-offset-2':
            (color?.value || color) === toRaw(inputValue?.value || inputValue),
          'aspect-square': borderRadius === '999px',
        }"
        :style="{
          borderRadius: borderRadius
        }"
        tabindex="0"
        :aria-label="`Select ${color.text || color.name}`"
        @click.prevent="inputValue = color"
        @keydown.enter="inputValue = color"
      >
        <tml-color-square
          class="inner rounded-sm"
          :class="{
            'min-w-[60px]': borderRadius !== '999px',
          }"
          :color="color"
          :disable-tooltip="disableTooltips"
          :style="{
            borderRadius: borderRadius,
          }"
        />
      </button>
    </div>
    <div v-if="displayError" class="message">
      Please select a colour
    </div>
  </div>
</template>

<script>
import {isObject} from 'lodash';
import {toRaw} from 'vue';

import {default as mixins} from './form-mixins';

export default {
  name: 'TmlColorSelector',
  mixins: [mixins],
  props: {
    preSelected: {
      type: [String, Object],
      required: false,
    },

    /**
     * Disabled emits 'disabled' on submit
     */
    disabled: Boolean,

    /**
     * A unique name to identify and register the TmlForm element as in the Vuex
     * store
     */
    fieldName: {
      type: String,
      required: true,
    },

    /**
     * Pass through validation rules to live validate user input
     * for more, see https://baianat.github.io/vee-validate/guide/
     */
    validationRules: Object,

    colors: Array,

    disableTooltips: {
      type: Boolean,
      default: false,
    },

    itemsPerRow: {
      type: Object,
      default: () => ({
        xs: 6,
      }),
    },

    borderRadius: {
      type: String,
      default: null,
    },
  },

  computed: {
    formattedColors() {
      return this.colors.map(color => {
        if (typeof color === 'string') {
          return color;
        }

        return {
          text: color.text,
          value: color.value,
          name: color.value,
          background: color.background,
        };
      });
    },

    colorsIncludesPreSelected() {
      if (isObject(this.preSelected)) {
        return this.colors.some(
          color =>
            color.text === this.preSelected.text &&
            color.value === this.preSelected.value
        );
      }

      return this.colors.includes(this.preSelected);
    },
  },

  created() {
    this.registerField(this.fieldName, {
      value: '',
      isValid: false,
    });
  },

  mounted() {
    if (this.preSelected && this.colorsIncludesPreSelected) {
      this.inputValue = this.preSelected;
    }
  },

  methods: {
    toRaw,
  },
};
</script>

<style scoped lang="scss">
.tml-color-selector {
  position: relative;

  .color {
    cursor: pointer;

    .inner {
      border-color: var(--tml-text-color);
    }
  }

  .inner {
    width: 100%;
    height: 100%;
    border: 1px solid $primary-color;
    background-color: $background-color;
    overflow: hidden;
  }

  .message {
    position: absolute;
    right: 0.5em;
    bottom: -12px;
    background-color: #fff;
    font-size: 12px;
    background-color: var(--tml-page-background-color);
  }

  &.error {
    border-bottom: 1px solid var(--tml-error-color);

    .color .inner {
      border-color: var(--tml-error-color);
    }

    .message {
      color: var(--tml-error-color);
    }
  }
}
</style>
